<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'

definePageMeta({
    layout: false,
    middleware: [
        'landing-page',
        'home-landing-page',
    ],
    layoutBackgroundColor: '#0d2018',
})

const { data: pageData } = await usePageData('frontpage')

const { getHead, getSeoMeta } = useStrapiSeo(
    pageData.value?.seo || {
        metaTitle: 'Play Fun Slots & Bingo at Lux Casino Online Casino',
        metaDescription: 'Join Lux Casino™ Today. Play Exclusive Slots Online & New Bingo Games. BeGambleAware.org. T&Cs apply. 18+. Play Safe. Sign Up Now:',
    }
)

useHead(getHead())

useSeoMeta(getSeoMeta())
</script>

<template>
    <div class="landing-page">
        <UIApp>
            <LandingPageNavBar />

            <main class="main">
                <LandingPageHeroLux />

                <StickyTerms
                    title="Significant Conditions"
                    is-landing-page
                    :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 50 }"
                />

                <LazyVisibleNimbusStrapiDynamicRenderer
                    v-if="!isEmpty(pageData?.dynamicContent)"
                    :content="pageData?.dynamicContent"
                    :max-width="1024"
                    color="#fff"
                    hydrate-on-visible
                />

                <section class="section section--dark join__terms">
                    <div class="section__content">
                        <StickyTerms
                            title="Bonus terms"
                            is-landing-page
                            force-full-terms
                        />
                    </div>
                </section>
            </main>

            <div class="landing-page__footer">
                <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
            </div>
        </UIApp>
    </div>
</template>

<style scoped lang="scss">
$body-bg-light: #0d2018;
$body-bg-dark: #091510;
$yellow: #f0bc00;
$content-width: 1024px;

.landing-page {
    --landing-page-bg: #{$body-bg-light};
    --sticky-terms-background: #0d2018;
    --sticky-terms-color: white;
    --login-button-color: #fff;
    // Hero background shows through the nav — override the default HT blue gradient
    // so the nav doesn't render as an opaque blue stripe on the dark green lux page.
    --nav-background: transparent;

    .theme-provider {
        --background: var(--landing-page-bg);
    }

    &__footer {
        padding-top: 50px;
        background-color: $body-bg;
    }
}

.dynamic-content {
    // Some CMS-rendered blocks (e.g. .fullscreen-video) use
    // `margin: 0 -15px` on mobile to break out of the platform's standard
    // gutter and reach the viewport edge. lc-uk doesn't add that gutter
    // here, so the negative margin would overrun the viewport. Clip the
    // mobile overflow to the container — sections that don't break out
    // are unaffected, and at desktop where the negative margin is
    // removed by the section's own media query the layout is flush
    // without the 15px inset that an actual padding gutter would force.
    overflow-x: clip;

    > :deep(:has(.usp-bar)) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    > :deep(:has(.winners-sidebar)) {
        padding-bottom: 0 !important;
    }

    // Vertical breathing room around content sections, but NOT around
    // .fullscreen-video — that block sets its own height via
    // `aspect-ratio: 16/9` and the iframe is sized to the full section
    // width. Adding top/bottom padding inflates the section's border
    // box and the iframe overflows below it (peeking under the next
    // section). Mirrors the spinking-uk pattern.
    > :deep(:not(.panel__body):not(.fullscreen-video)) {
        padding: 40px 15px;

        @media (min-width: $screen-lg) {
            padding-top: 50px;
            padding-bottom: 50px;
        }
    }

    // The CMS .fullscreen-video gradient fades to core's `$body-bg`
    // SCSS variable (#0d2018, same as `--background`), but the actual
    // theme body bg is the luxcasino gradient that settles on #00221b
    // by the time the video appears below the hero — so the default
    // gradient ends on the wrong shade of green and the seam is
    // visible. Repaint with the colour the video sits on.
    :deep(.fullscreen-video .video-gradient) {
        background: linear-gradient(to bottom, transparent 30%, #00221b 100%);

        @media (min-width: 1280px) {
            &::before {
                background: linear-gradient(to right, transparent 80%, #00221b 100%);
            }

            &::after {
                background: linear-gradient(to left, transparent 80%, #00221b 100%);
            }
        }
    }

    // CMS headings (e.g. "Members Only", "Curated Collection") default to
    // a brighter green that doesn't match the theme. Override with the
    // on-secondary-soft token — the lighter green tuned for AA contrast
    // on this dark page background.
    :deep(h2) {
        color: var(--on-secondary-soft);
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }

    :deep(.text) {
        max-width: 800px;
    }

    :deep(.blog-posts) {
        max-width: 800px;
    }

    :deep(.media-text__text) {
        a {
            color: inherit;
        }
    }
    :deep(.media-text__title) {
        a {
            color: inherit;
        }
    }
}

.main {
    // Establish a block formatting context so the hero's margin-top: -108px
    // cannot collapse upward through <main> into body. Without this, the negative
    // margin reaches body (position: relative from core), shifting the navbar's
    // absolute containing block up by 108px and pushing the nav off-screen.
    display: flow-root;

    .section {
        &__content {
            width: 100%;
            max-width: $content-width;
            margin: auto;
        }

        &--dark {
            background-color: var(--body-bg-dark);
        }
    }

    a:not(.btn) {
        color: inherit;
        text-decoration: underline;
    }

    .join {
        padding-top: 2rem;
        padding-bottom: 3rem;
        color: $white;
        text-align: center;

        @media (min-width: $screen-md) {
            padding-bottom: 4rem;
        }

        .section__content {
            display: grid;
            grid-template-columns: 1fr;
            justify-items: center;
        }

        &__terms {
            --sticky-terms-color: white;

            padding-bottom: 3rem;

            @media (min-width: $screen-md) {
                padding-bottom: 4rem;
            }

            :deep(.offer-terms__title) {
                margin-bottom: 3px;
                font-size: 12px;
            }
        }
    }
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
    }
}
</style>
